import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { usePrefsSync } from '@/lib/prefs/use-prefs-sync';
import { parseSymbolParam } from '@/lib/dashboard/symbol-param';
import { isMarket, type Market } from '@/lib/market/types';
import { resolveInitialSymbol, useSymbolStore } from '@/stores/symbolStore';

/**
 * =============================================================================
 *  `/:market/dashboard` —— ARCHITECTURE §10.5 的那一頁。
 *
 *  `?symbol` 是 typed search param，因為它是**產品狀態**：使用者會把
 *  `/tw/dashboard?symbol=2330` 貼給別人（`?as_of` 在 __root.tsx 宣告，這裡繼承）。
 *
 *  **這個檔案裡沒有任何一個 `key={...}`。** §10.2 規則 3 要求換股票時不得 remount，
 *  而在 TanStack Router 底下最容易違反它的方式就是「為了重置某個內部狀態，
 *  順手在頁面元件上加一個 key={symbol}」。加上去之後，每換一檔股票整棵子樹
 *  重建一次，圖表的縮放與捲動位置就跟著沒了——而那正是 §10.4.1 規則 1
 *  在元件內部拼命保住的東西。
 *
 *  守這件事的是 `tools/verify-chart-invariants.mjs`（`npm run lint` 的一部分，
 *  所以 CI 會跑到）。它掃 route → DashboardPage → ChartSlot 這條鏈上有沒有
 *  `key={…symbol…}`，並且斷言 `PriceChart` 的 EFFECT A 相依陣列是空的。
 *  這兩件事是「換股票不 remount、prop 變不重建」唯一的兩條真實迴歸路徑，
 *  而它們都會通過 tsc、eslint、vitest 與 pytest。
 * ========================================================================== */

export interface DashboardSearch {
  symbol?: string;
}

export const Route = createFileRoute('/$market/dashboard')({
  validateSearch: (search: Record<string, unknown>): DashboardSearch => {
    const symbol = parseSymbolParam(search['symbol']);
    return symbol === undefined ? {} : { symbol };
  },
  component: DashboardRoute,
});

function DashboardRoute() {
  const { market: rawMarket } = Route.useParams();
  const { symbol: urlSymbol } = Route.useSearch();
  const navigate = useNavigate();
  const market: Market = isMarket(rawMarket) ? rawMarket : 'tw';
  const setSymbol = useSymbolStore((s) => s.setSymbol);

  // 沒帶 ?symbol 時用「這個市場上次看的那一檔」。第一次進站則是 2330 / AAPL。
  const symbol = resolveInitialSymbol(market, urlSymbol);

  // URL → store。**唯一的寫入方向**（見 stores/symbolStore.ts 的那張圖）。
  useEffect(() => {
    setSymbol(market, symbol);
  }, [market, symbol, setSymbol]);

  // 不再把隱含的 symbol 補寫回 URL：`?symbol` 存在＝使用者明確選了一檔（直接顯示個股頁），
  // 不存在＝停在「今日重點」。

  usePrefsSync(market);

  return (
    <DashboardPage
      market={market}
      symbol={symbol}
      symbolFromUrl={urlSymbol !== undefined}
      onSymbolChange={(next) => {
        // 代號決定市場（在美股搜尋框打 2330 也該work），市場決定 [data-market]。
        void navigate({
          to: '/$market/dashboard',
          params: { market: next.market },
          search: (prev: Record<string, unknown>) => ({ ...prev, symbol: next.symbol }),
        });
      }}
    />
  );
}
